import { Task } from 'konect-model';
import { nanoid } from 'nanoid';

/**
 * Detects the indentation type and size used in the text
 * @param lines Array of non-empty lines
 * @returns Object with type ('tab' | 'space') and size (number of spaces per level)
 */
function detectIndentation(lines: string[]): { type: 'tab' | 'space'; size: number } {
  // Find the first indented line
  for (const line of lines) {
    if (line.startsWith('\t')) {
      return { type: 'tab', size: 1 };
    }
    if (line.startsWith(' ')) {
      // Count leading spaces
      const spaces = line.match(/^ +/)?.[0].length || 0;
      if (spaces > 0) {
        return { type: 'space', size: spaces };
      }
    }
  }
  // Default to tab if no indentation found
  return { type: 'tab', size: 1 };
}

/**
 * Calculates the depth (hierarchy level) of a line
 * @param line The text line
 * @param indentType 'tab' or 'space'
 * @param indentSize Number of spaces per level (only used for space indentation)
 * @returns The depth level (0 = root)
 */
function calculateDepth(line: string, indentType: 'tab' | 'space', indentSize: number): number {
  if (indentType === 'tab') {
    const match = line.match(/^\t*/);
    return match ? match[0].length : 0;
  } else {
    const match = line.match(/^ */);
    const spaces = match ? match[0].length : 0;
    return Math.floor(spaces / indentSize);
  }
}

/**
 * Parses tab-indented or space-indented hierarchy text into a Task tree
 * @param text Tab-indented or space-indented text (e.g., from PowerPoint Smart Art)
 * @returns Root Task with complete hierarchy
 * @throws Error if text is empty or malformed
 */
export function parseTabIndentedHierarchy(text: string): Task {
  // Split into lines and filter out empty lines
  const lines = text.split('\n')
    .map(line => line.trimEnd()) // Remove trailing whitespace
    .filter(line => line.trim().length > 0);

  if (lines.length === 0) {
    throw new Error('Input text is empty');
  }

  // Detect indentation type and size
  const { type: indentType, size: indentSize } = detectIndentation(lines);

  // Check that first line is not indented (must be root)
  const firstLineDepth = calculateDepth(lines[0], indentType, indentSize);
  if (firstLineDepth > 0) {
    throw new Error('First line must not be indented (it should be the root task)');
  }

  // Parse lines into objects with depth and name
  interface ParsedLine {
    depth: number;
    name: string;
    task: Task;
  }

  const parsedLines: ParsedLine[] = lines.map(line => {
    const depth = calculateDepth(line, indentType, indentSize);
    const name = line.trim();

    const task = new Task();
    task.id = nanoid();
    task.name = name;
    task.nr = '';
    task.agents = [];
    task.rules = [];
    task.subtasks = [];
    task.parent = '';
    task.type = [];
    task.states = [];

    return { depth, name, task };
  });

  // Build hierarchy using a stack-based approach
  const stack: ParsedLine[] = [];

  for (let i = 0; i < parsedLines.length; i++) {
    const current = parsedLines[i];

    // Pop stack until we find the parent level
    while (stack.length > 0 && stack[stack.length - 1].depth >= current.depth) {
      stack.pop();
    }

    // If stack is not empty, the top is the parent
    if (stack.length > 0) {
      const parent = stack[stack.length - 1];
      current.task.parent = parent.task.id;
      parent.task.subtasks.push(current.task);
    } else if (i > 0) {
      // If stack is empty and not the first line, there's a structure issue
      console.warn(`Line ${i + 1} ("${current.name}") has inconsistent indentation. Treating as root level.`);
    }

    // Push current onto stack
    stack.push(current);
  }

  // Return the root task (first line)
  return parsedLines[0].task;
}
